/**
 * ApiResult / AppError adapter（ADR-004）
 * 将 axios / mock promise 归一为 { ok, data } | { ok, error }，组件只识别 ok / error.code / error.message。
 */
import { ERROR_CODES } from '@/2D3DEditer/ports/errorCodes'

// 单一事实源在 @/2D3DEditer/ports/errorCodes；此处 re-export 保持既有 `@/api/apiResultAdapter` 消费者兼容。
export { ERROR_CODES }

const KNOWN_ERROR_CODES = new Set(Object.values(ERROR_CODES))

const DEFAULT_MESSAGES = {
  [ERROR_CODES.VALIDATION_ERROR]: 'Validation failed',
  [ERROR_CODES.MOCK_NOT_FOUND]: 'Mock data not found',
  [ERROR_CODES.STORAGE_READ_FAILED]: 'Failed to read from storage',
  [ERROR_CODES.STORAGE_WRITE_FAILED]: 'Failed to write to storage',
  [ERROR_CODES.EXPORT_FAILED]: 'Export failed',
  [ERROR_CODES.NETWORK_ERROR]: 'Network request failed'
}

const RETRYABLE_CODES = new Set([
  ERROR_CODES.NETWORK_ERROR,
  ERROR_CODES.STORAGE_READ_FAILED
])

/**
 * @param {unknown} data
 * @param {string} [requestId]
 * @returns {{ ok: true, data: unknown, requestId?: string }}
 */
export function ok(data, requestId) {
  const result = { ok: true, data }
  if (requestId) result.requestId = requestId
  return result
}

/**
 * @param {unknown} error
 * @param {string} [requestId]
 * @returns {{ ok: false, error: AppError, requestId?: string }}
 */
export function fail(error, requestId) {
  const result = { ok: false, error: toAppError(error) }
  if (requestId) result.requestId = requestId
  return result
}

/**
 * @typedef {Object} AppError
 * @property {string} code
 * @property {string} message
 * @property {Record<string, unknown>} [details]
 * @property {boolean} [retryable]
 */

/**
 * @param {unknown} input
 * @returns {AppError}
 */
export function toAppError(input) {
  if (isAppError(input)) {
    return normalizeAppError(input)
  }

  if (typeof input === 'string') {
    return buildAppError(ERROR_CODES.NETWORK_ERROR, input)
  }

  if (input instanceof Error) {
    const code = resolveExplicitCode(input) || ERROR_CODES.NETWORK_ERROR
    const message = input.message || DEFAULT_MESSAGES[code]
    const details = input.details && typeof input.details === 'object' ? input.details : undefined
    return buildAppError(code, message, details)
  }

  if (input && typeof input === 'object') {
    if (isAxiosError(input)) {
      return appErrorFromAxios(input)
    }

    const code = resolveExplicitCode(input) || ERROR_CODES.NETWORK_ERROR
    const message = extractMessage(input) || DEFAULT_MESSAGES[code]
    const details = input.details && typeof input.details === 'object' ? input.details : undefined
    return buildAppError(code, message, details)
  }

  return buildAppError(ERROR_CODES.NETWORK_ERROR, DEFAULT_MESSAGES[ERROR_CODES.NETWORK_ERROR])
}

/**
 * @param {Promise<unknown>} promise
 * @returns {Promise<{ ok: true, data: unknown } | { ok: false, error: AppError }>}
 */
export function adapt(promise) {
  return Promise.resolve(promise)
    .then(data => ok(data))
    .catch(error => fail(error))
}

function isAppError(value) {
  return !!(
    value &&
    typeof value === 'object' &&
    typeof value.code === 'string' &&
    typeof value.message === 'string' &&
    KNOWN_ERROR_CODES.has(value.code)
  )
}

function normalizeAppError(error) {
  const code = KNOWN_ERROR_CODES.has(error.code) ? error.code : ERROR_CODES.NETWORK_ERROR
  const message = error.message || DEFAULT_MESSAGES[code]
  const details = error.details && typeof error.details === 'object' ? error.details : undefined
  return buildAppError(code, message, details)
}

function buildAppError(code, message, details) {
  /** @type {AppError} */
  const appError = {
    code,
    message: message || DEFAULT_MESSAGES[code] || DEFAULT_MESSAGES[ERROR_CODES.NETWORK_ERROR]
  }

  if (details) appError.details = details
  if (RETRYABLE_CODES.has(code)) appError.retryable = true

  return appError
}

function isAxiosError(error) {
  return !!(
    error &&
    (error.isAxiosError || error.response || (error.request && !error.response))
  )
}

function resolveExplicitCode(source) {
  if (!source || typeof source !== 'object') return null

  const candidates = [
    source.code,
    source.errorCode,
    source.response && source.response.data && source.response.data.code
  ]

  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i]
    if (typeof candidate === 'string' && KNOWN_ERROR_CODES.has(candidate)) {
      return candidate
    }
  }

  return null
}

function extractMessage(source) {
  if (!source) return ''
  if (typeof source === 'string') return source
  if (typeof source.message === 'string' && source.message) return source.message

  const responseData = source.response && source.response.data
  if (typeof responseData === 'string') return responseData
  if (responseData && typeof responseData === 'object') {
    return responseData.message || responseData.msg || responseData.error || ''
  }

  return ''
}

function inferCodeFromAxios(error) {
  const explicitCode = resolveExplicitCode(error)
  if (explicitCode) return explicitCode

  const status = error.response && error.response.status
  if (status === 404) return ERROR_CODES.MOCK_NOT_FOUND
  if (status === 400 || status === 422) return ERROR_CODES.VALIDATION_ERROR
  if (!error.response) return ERROR_CODES.NETWORK_ERROR

  return ERROR_CODES.NETWORK_ERROR
}

function appErrorFromAxios(error) {
  const code = inferCodeFromAxios(error)
  const message = extractMessage(error) || DEFAULT_MESSAGES[code]
  const responseData = error.response && error.response.data
  const details =
    responseData && typeof responseData === 'object' && !Array.isArray(responseData)
      ? responseData.details || responseData.errors || undefined
      : undefined

  return buildAppError(code, message, details)
}
